import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ShieldCheck, BadgePercent, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { PricingPlanGrid } from "@/components/PricingPlanGrid";

export const Route = createFileRoute("/_authenticated/plans")({
  head: () => ({
    meta: [
      { title: "Plans & billing — Lumvio" },
      {
        name: "description",
        content:
          "Manage your Lumvio subscription. Every plan includes Payment Protection on milestones and 0% commission on your freelance earnings.",
      },
    ],
  }),
  component: PlansPage,
});

function PlansPage() {
  const { profile } = useAuth();
  const { t } = useTranslation();

  const currentTier = profile?.subscription_tier ?? "free";
  const kycVerified = profile?.kyc_status === "verified";

  const chips = [
    { icon: BadgePercent, label: t("plansPage.chipCommission", { defaultValue: "0% commission on earnings" }) },
    { icon: ShieldCheck, label: t("plansPage.chipProtection", { defaultValue: "Payment Protection included" }) },
    { icon: RefreshCw, label: t("plansPage.chipCancel", { defaultValue: "Cancel anytime" }) },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <header className="relative overflow-hidden rounded-3xl border border-border/60 bg-card/60 p-6 sm:p-8 backdrop-blur-xl">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(600px circle at 15% -10%, color-mix(in oklch, var(--primary) 16%, transparent), transparent 60%)",
          }}
          aria-hidden
        />
        <div className="relative space-y-3">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight bg-gradient-to-b from-foreground to-foreground/65 bg-clip-text text-transparent pb-0.5">
            {t("plansPage.title", { defaultValue: "Plans & billing" })}
          </h1>
          <p className="max-w-2xl text-sm sm:text-base leading-relaxed text-muted-foreground">
            {t("plansPage.subtitle", {
              defaultValue:
                "Choose the plan that fits your client workload. Payment Protection and 0% commission are included on every plan.",
            })}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/40 px-3 py-1 text-xs text-muted-foreground backdrop-blur-sm"
              >
                <Icon className="h-3.5 w-3.5 text-primary" aria-hidden />
                {label}
              </span>
            ))}
          </div>
        </div>
      </header>

      <PricingPlanGrid
        currentTier={currentTier}
        kycVerified={kycVerified}
        showBadge={false}
        showTitle={false}
        showFootnote
        className="py-8"
      />
    </div>
  );
}
